import { useEffect, useRef, useState } from 'react';
import { activeLoggers } from '../hooks/useLoggers';
import LoggerRoutes from './LoggerRoutes';
import { LoggerIcon } from './LoggerIcon';
import { createPortal } from 'react-dom';
import { portalRoot } from './TourGuide';
import { toast } from 'sonner';
import { X, Search, Crosshair, Check, Navigation, Camera, MapPin, SkipForward, Trash2, Route, Play, Share2 } from 'lucide-react';
import { geoSearch } from '../lib/geocode';
import { routeLink } from '../lib/route';
import { uid, compressImage } from '../lib/model';
import { H } from '../hooks/useLoggers';
import { buzz } from '../lib/glove';
import LoggerCollect from './LoggerCollect';
import { usePos, dist, fmtDist, ModePick, PosPick, pickAt, startRound } from './lpShared';
import { ShareRoundDialog } from './RoundShare';
import { HelpHere } from './TourGuide';
import LoggerSuggest from './LoggerSuggest';

// logger deployment: plan in the office (address -> crosshair -> confirm -> next), deploy outside (navigate -> arrive -> set with GPS)
export const openLoggerPlan = () => window.dispatchEvent(new Event('giss-lgplan-open'));
// mode for planned loggers: permanent or lift & shift with days; next logger takes the last choice
const LPM = 'giss_lp_mode';
const lastMode = () => { try { return { mode: 'lift', days: 3, ...JSON.parse(localStorage.getItem(LPM) || '{}') }; } catch { return { mode: 'lift', days: 3 }; } };
export const modeLabel = (t, p) => (p.mode === 'perm' ? t.lpPerm : `${t.lpLift} · ${p.days || 1} ${t.lgDays}`);

function ShareBtn({ t, plan, project }) {
  const [open, setOpen] = useState(false);
  if (!plan.some((p) => !p.doneAt)) return null;
  return <><button className="btn btn-outline" data-testid="lp-share-round-btn" onClick={() => setOpen(true)}><Share2 size={16} /> {t.rsShare}</button>{open && <ShareRoundDialog t={t} project={project} plan={plan} onClose={() => setOpen(false)} />}</>;
}

function Plan({ t, lang, plan, setPlan, mapRef, onStart, project, setSpacing }) {
  const [name, setName] = useState('');
  const [md, setMd] = useState(lastMode);
  const pickMode = (v) => { setMd(v); localStorage.setItem(LPM, JSON.stringify(v)); };
  const [addr, setAddr] = useState('');
  const [hits, setHits] = useState([]);
  const find = async () => { if (!addr.trim()) return; try { const r = await geoSearch(addr, lang); setHits(r); if (r[0]) mapRef.current.setView(r[0].lat, r[0].lon, 19); else toast.error(t.lpNoHit); } catch { toast.error(t.lpNoHit); } };
  const confirm = () => {
    const c = mapRef.current.getCenter();
    setPlan([...plan, { id: uid('lp'), name: name.trim() || `${t.lgTitle} ${plan.length + 1}`, address: addr.trim(), lat: c.lat, lon: c.lng ?? c.lon, mode: md.mode, days: md.days }]);
    buzz(45); toast.success(t.lpAdded); setName(''); setAddr(''); setHits([]);
  };
  return (
    <>
      <p className="muted lp-hint">{t.lpPlanHint}</p>
      <input data-testid="lp-name-input" placeholder={`${t.lgName} (${t.lgTitle} ${plan.length + 1})`} value={name} onChange={(e) => setName(e.target.value)} />
      <div className="lp-row">
        <input data-testid="lp-addr-input" placeholder={t.lpAddr} value={addr} onChange={(e) => setAddr(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && find()} />
        <button className="btn btn-dark" data-testid="lp-addr-search" onClick={find}><Search size={16} /></button>
      </div>
      {hits.length > 1 && <select data-testid="lp-addr-hits" onChange={(e) => { const h = hits[+e.target.value]; mapRef.current.setView(h.lat, h.lon, 19); }}>{hits.map((h, i) => <option key={h.name} value={i}>{h.name}</option>)}</select>}
      <ModePick t={t} v={md} onChange={pickMode} pre="lp" />
      <button className="btn btn-primary lp-big" data-testid="lp-confirm-btn" onClick={confirm}><Crosshair size={18} /> {t.lpConfirm}</button>
      <LoggerSuggest lang={lang} project={project} plan={plan} setPlan={setPlan} mapRef={mapRef} md={md} setSpacing={setSpacing} />
      <ol className="lp-list" data-testid="lp-list">
        {plan.map((p, i) => <li key={p.id} className={p.doneAt ? 'done' : ''}><b>{p.name}</b> <small data-testid={`lp-item-mode-${i}`}>{modeLabel(t, p.mode ? p : { mode: 'lift', days: 1 })}{p.address ? ` · ${p.address}` : ''}</small><button className="icon-btn danger" data-testid={`lp-del-${i}`} onClick={() => setPlan(plan.filter((x) => x.id !== p.id))}><Trash2 size={13} /></button></li>)}
      </ol>
      {plan.some((p) => !p.doneAt) && <button className="btn btn-green lp-big" data-testid="lp-start-round-btn" onClick={onStart}><Play size={18} /> {t.lrStart} ({plan.filter((p) => !p.doneAt).length})</button>}
      <ShareBtn t={t} plan={plan} project={project} />
    </>
  );
}

// ordered round: tap an entry to go there next; full round as Google Maps route
function Round({ t, plan, setPlan, next }) {
  const [legs, setLegs] = useState([]);
  const open = plan.filter((p) => !p.doneAt);
  const pick = (p) => { if (!p.doneAt && p.id !== next?.id) setPlan([...plan.filter((x) => x.doneAt), p, ...open.filter((x) => x.id !== p.id)]); };
  return (
    <div className="lp-round" data-testid="lp-round">
      <b className="lc-lbl"><Route size={14} /> {t.lrTitle} · {plan.length - open.length}/{plan.length}</b>
      <ol className="lp-round-list" data-testid="lp-round-list">
        {plan.map((p, i) => <li key={p.id} className={p.doneAt ? 'done' : p.id === next?.id ? 'cur' : ''} data-testid={`lp-round-item-${i}`} onClick={() => pick(p)}><i>{p.doneAt ? '✓' : i + 1}</i>{p.name}<small>{p.mode === 'perm' ? t.lpPerm : `${p.days || 1} ${t.lgDays}`}</small></li>)}
      </ol>
      {open.length > 0 && <button className="btn btn-primary btn-sm" data-testid="lp-round-route-btn" onClick={async () => { const l = await startRound(plan, setPlan, true); setLegs(l.length > 1 ? l : []); }}><Route size={14} /> {t.lrRoute} ({open.length})</button>}
      {legs.map((l, i) => <a key={l.url} className="btn btn-outline btn-sm" data-testid={`lp-round-leg-${i}`} href={l.url} target="_blank" rel="noreferrer"><Route size={13} /> {t.routeBtn} {i + 1}/{legs.length}</a>)}
    </div>
  );
}

function Deploy({ t, plan, setPlan, updateProject, repId, mapRef, project }) {
  const next = plan.find((p) => !p.doneAt);
  const pos = usePos(!!next);
  const [name, setName] = useState('');
  const [photo, setPhoto] = useState(null);
  const [md, setMd] = useState({ mode: 'lift', days: 1 });
  const [src, setSrc] = useState(null);
  const camRef = useRef(null), near = useRef(false);
  const d = next && pos ? dist(pos, next) : null, here = d != null && d < 30;
  const use = pos ? src || (here ? 'gps' : 'cross') : 'cross';
  useEffect(() => { setName(next?.name || ''); setPhoto(null); setSrc(null); near.current = false; setMd({ mode: next?.mode || 'lift', days: next?.days || 1 }); if (next) mapRef.current?.setView(next.lat, next.lon, 19); }, [next?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (here && !near.current) { near.current = true; navigator.vibrate?.([200, 80, 200]); toast.success(t.lpArrived, { id: 'lp-arr' }); } }, [here, t]);
  if (!next) return <><p className="lp-done" data-testid="lp-all-done">✅ {t.lpAllDone}</p><LoggerRoutes t={t} project={project} /></>;
  const set = () => {
    const at = pickAt(use, pos, mapRef), now = Date.now();
    const l = { id: uid('log'), name: name.trim() || next.name, mode: md.mode, durMs: md.mode === 'perm' ? null : md.days * 24 * H, lat: at.lat, lon: at.lon, placedAt: now, dueAt: md.mode === 'perm' ? null : now + md.days * 24 * H, reportId: repId, history: [], address: next.address, planned: { lat: next.lat, lon: next.lon }, ...(photo ? { photo } : {}) };
    updateProject((p) => ({ ...p, loggers: [...(p.loggers || []), l] }));
    setPlan(plan.map((p) => (p.id === next.id ? { ...p, doneAt: now, loggerId: l.id } : p)));
    buzz([60, 50, 120]); toast.success(`${t.lgPlaced}: ${l.name}`);
    mapRef.current.setView(l.lat, l.lon, 19);
  };
  const skip = () => setPlan([...plan.filter((p) => p.id !== next.id), next]);
  return (
    <div className={`lp-deploy ${here ? 'is-here' : ''}`} data-testid="lp-deploy">
      <Round t={t} plan={plan} setPlan={setPlan} next={next} />
      <small className="muted">{plan.filter((p) => p.doneAt).length + 1} / {plan.length}</small>
      <b className="lp-next" data-testid="lp-next-name">{next.name}</b>
      {next.address && <span className="muted">{next.address}</span>}
      <p className="lp-dist" data-testid="lp-dist">{d == null ? t.skWait : here ? `📍 ${t.lpArrived}` : fmtDist(d)}</p>
      <div className="lp-row">
        <a className="btn btn-primary" data-testid="lp-nav-btn" href={routeLink(next)} target="_blank" rel="noreferrer"><Navigation size={16} /> {t.alNav}</a>
        <button className="btn btn-outline" data-testid="lp-show-btn" onClick={() => mapRef.current.setView(next.lat, next.lon, 19)}><MapPin size={16} /></button>
        <button className="btn btn-outline" data-testid="lp-skip-btn" onClick={skip} title={t.lpSkip}><SkipForward size={16} /></button>
      </div>
      <input data-testid="lp-set-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t.lgName} />
      <ModePick t={t} v={md} onChange={setMd} pre="lpd" />
      <PosPick t={t} pos={pos} src={use} setSrc={setSrc} pre="lpd" />
      <button className="btn btn-outline" data-testid="lp-photo-btn" onClick={() => camRef.current.click()}><Camera size={16} /> {photo ? '✓ ' : ''}{t.btnPhoto}</button>
      <input ref={camRef} type="file" accept="image/*" capture="environment" hidden data-testid="lp-photo-input" onChange={async (e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setPhoto(await compressImage(f)); }} />
      <button className="btn btn-green lp-big" data-testid="lp-set-btn" onClick={set}><Check size={20} /> {t.lpSet} ({use === 'gps' ? 'GPS' : t.lpCross})</button>
    </div>
  );
}

export default function LoggerPlan({ t, lang, project, updateProject, repId, mapRef }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('plan');
  const [focus, setFocus] = useState(null);
  const [, setFs] = useState(0);
  useEffect(() => {
    const on = (e) => { setOpen(true); if (e.detail?.tab) { setTab(e.detail.tab); setFocus(e.detail.id || null); } }, fs = () => setFs((n) => n + 1);
    window.addEventListener('giss-lgplan-open', on); document.addEventListener('fullscreenchange', fs);
    return () => { window.removeEventListener('giss-lgplan-open', on); document.removeEventListener('fullscreenchange', fs); };
  }, []);
  if (!open) return null;
  const plan = project.loggerPlan || [];
  const setPlan = (list) => updateProject((p) => ({ ...p, loggerPlan: list }));
  return createPortal(
    <div className="lp-panel mask" data-testid="logger-plan">
      <div className="ov-panel-head mask-keep"><b><LoggerIcon size={16} /> {t.lpTitle}</b><HelpHere t={t} area="lp" className="icon-btn" compact /><button className="icon-btn" data-testid="lp-close-btn" onClick={() => { setOpen(false); setFocus(null); }}><X size={16} /></button></div>
      <div className="lp-tabs">
        <button className={`chip seg ${tab === 'plan' ? 'on' : ''}`} data-testid="lp-tab-plan" onClick={() => setTab('plan')}>{t.lpPlan} ({plan.length})</button>
        <button className={`chip seg ${tab === 'deploy' ? 'on' : ''}`} data-testid="lp-tab-deploy" onClick={() => setTab('deploy')}>{t.lpDeploy} ({plan.filter((p) => !p.doneAt).length})</button>
        <button className={`chip seg ${tab === 'collect' ? 'on' : ''}`} data-testid="lp-tab-collect" onClick={() => setTab('collect')}>{t.lcTab} ({activeLoggers(project).filter((l) => !l.missingAt).length})</button>
      </div>
      {tab === 'plan' && <Plan t={t} lang={lang} plan={plan} setPlan={setPlan} mapRef={mapRef} project={project} setSpacing={(v) => updateProject((p) => ({ ...p, lgSpacing: v }))} onStart={() => { startRound(plan, setPlan); setTab('deploy'); }} />}
      {tab === 'deploy' && <Deploy t={t} plan={plan} setPlan={setPlan} updateProject={updateProject} repId={repId} mapRef={mapRef} project={project} />}
      {tab === 'collect' && <LoggerCollect t={t} project={project} updateProject={updateProject} mapRef={mapRef} focusId={focus} />}
    </div>,
    portalRoot(),
  );
}
